/**
 * Pure currency helpers shared by the public landing page and the in-app
 * billing flow. No Firebase imports — safe to use anywhere.
 *
 * Pricing is set as round local numbers (not FX-derived): US pays USD, Canada
 * pays CAD. Default is detected from locale/timezone and is always overridable.
 */

export type Currency = "usd" | "cad";
export type Plan = "monthly" | "annual";

const CANADIAN_TZ = [
  "Toronto", "Vancouver", "Edmonton", "Winnipeg", "Halifax", "Regina",
  "St_Johns", "Moncton", "Glace_Bay", "Goose_Bay", "Yellowknife",
  "Whitehorse", "Dawson", "Dawson_Creek", "Iqaluit", "Rankin_Inlet",
  "Resolute", "Cambridge_Bay", "Inuvik", "Atikokan", "Blanc-Sablon",
  "Fort_Nelson", "Creston", "Swift_Current",
];

/**
 * Detect billing currency: Canada → CAD, every other country → USD.
 *
 * The device timezone is the location signal. The browser language is NOT:
 * an `en-CA` laptop in Tashkent is not in Canada, so language is only a
 * fallback when no timezone is available. The build-time prerender (no
 * window) always gets USD, so the build machine's location never ends up
 * baked into the static /, /uz and /ru pages.
 */
export function detectCurrency(): Currency {
  if (typeof window === "undefined") return "usd";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz) return CANADIAN_TZ.some((city) => tz === `America/${city}`) ? "cad" : "usd";
    const lang = (navigator.language || "").toLowerCase();
    return lang.endsWith("-ca") ? "cad" : "usd";
  } catch {
    return "usd";
  }
}

/** Billing currency for an ISO country code (from Cloudflare's edge). */
export function currencyForCountry(country: string): Currency {
  return country.trim().toUpperCase() === "CA" ? "cad" : "usd";
}

/** Pull `loc=XX` out of a Cloudflare /cdn-cgi/trace response body. */
export function countryFromTrace(body: string): string | null {
  const m = /^loc=([A-Z]{2})$/m.exec(body);
  return m?.[1] ?? null;
}

/** Human-readable price strings per currency + plan. Keep in sync with the
 *  live Stripe prices in functions/src/billingConfig.ts. */
export const PRICE_DISPLAY: Record<
  Currency,
  { code: string; monthly: string; annual: string; annualPerMonth: string }
> = {
  usd: { code: "USD", monthly: "$99", annual: "$899", annualPerMonth: "$74.92" },
  cad: { code: "CAD", monthly: "CA$129", annual: "CA$1,159", annualPerMonth: "CA$96.58" },
};

export function currencyLabel(c: Currency): string {
  return c === "cad" ? "CA$" : "US$";
}
